'use client'

import { FormCard } from '../FormCard/FormCard'
import { FormEvoCategorySelect } from '@repo/ui/FormEvoCategorySelect'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormsStore } from '@repo/store/useFormsStore'
import MUIBox from '@mui/material/Box'
import MUICheckbox from '@mui/material/Checkbox'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

const VARIANTS = ['checkbox', 'radio']
const DEFAULT_PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 500

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

export const FormSearchAndSelect = ({
  variant,
  selectedFormIds,
  onSelectionChange,
  workTypeId,
  pageSize = DEFAULT_PAGE_SIZE,
  hideLinkedToWorktypeCheckbox = false,
  sx,
  cardsContainerSx
}) => {
  const { formCategories, getAllFormsBySearchCriteria, fetchAllFormCategories } = useFormsStore()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS)
  const [formCategoryId, setFormCategoryId] = useState(null)
  const [linkedToWorktype, setLinkedToWorktype] = useState(!hideLinkedToWorktypeCheckbox)

  const [forms, setForms] = useState([])
  const [totalCount, setTotalCount] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasError, setHasError] = useState(false)

  const sentinelRef = useRef(null)
  const requestIdRef = useRef(0)

  const showWorktypeFilter = Boolean(workTypeId)
  const effectiveWorkTypeId = showWorktypeFilter && linkedToWorktype ? workTypeId : null

  useEffect(() => {
    if (!formCategories || formCategories.length === 0) {
      fetchAllFormCategories().catch(() => {})
    }
  }, [formCategories, fetchAllFormCategories])

  const fetchPage = useCallback(
    async ({ startAtIndex, append }) => {
      const myRequestId = ++requestIdRef.current
      if (append) setIsLoadingMore(true)
      else setIsLoading(true)
      setHasError(false)

      try {
        const response = await getAllFormsBySearchCriteria({
          startAtIndex,
          pageSize,
          name: debouncedSearchTerm || null,
          description: debouncedSearchTerm || null,
          formCategoryId: formCategoryId ?? null,
          workTypeId: effectiveWorkTypeId
        })

        if (myRequestId !== requestIdRef.current) return

        const rows = Array.isArray(response) ? response : (response?.rows ?? response?.items ?? [])
        const total =
          typeof response?.totalCount === 'number'
            ? response.totalCount
            : typeof response?.total === 'number'
              ? response.total
              : null

        setForms((prev) => (append ? [...prev, ...rows] : rows))
        setTotalCount(total)
      } catch (error) {
        if (myRequestId !== requestIdRef.current) return
        setHasError(true)
      } finally {
        if (myRequestId === requestIdRef.current) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    },
    [getAllFormsBySearchCriteria, pageSize, debouncedSearchTerm, formCategoryId, effectiveWorkTypeId]
  )

  useEffect(() => {
    setForms([])
    setTotalCount(null)
    fetchPage({ startAtIndex: 0, append: false })
  }, [fetchPage])

  const hasMore = useMemo(() => {
    if (forms.length === 0) return false
    if (totalCount != null) return forms.length < totalCount
    return forms.length % pageSize === 0
  }, [forms.length, totalCount, pageSize])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMore || isLoading || isLoadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchPage({ startAtIndex: forms.length, append: true })
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, isLoading, isLoadingMore, forms.length, fetchPage])

  const handleCheckboxToggle = (formId) => () => {
    const key = String(formId)
    const current = Array.isArray(selectedFormIds) ? selectedFormIds : []
    const next = current.some((id) => String(id) === key)
      ? current.filter((id) => String(id) !== key)
      : [...current, formId]
    onSelectionChange(next)
  }

  const handleRadioChange = (event) => {
    onSelectionChange(event.target.value)
  }

  const checkboxSelected = useMemo(
    () => new Set((Array.isArray(selectedFormIds) ? selectedFormIds : []).map(String)),
    [selectedFormIds]
  )

  const renderCards = () =>
    forms.map((form) => (
      <FormCard
        key={form.formId}
        variant={variant}
        formId={form.formId}
        name={form.name}
        description={form.description}
        formCategoryId={form.formCategoryId}
        value={form.formId}
        checked={variant === 'checkbox' ? checkboxSelected.has(String(form.formId)) : undefined}
        onChange={variant === 'checkbox' ? handleCheckboxToggle(form.formId) : undefined}
      />
    ))

  return (
    <MUIStack spacing={2} sx={sx}>
      <MUIStack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
        <MUITextField
          label="Search"
          placeholder="Enter name or description"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{ flex: 1, bgcolor: 'common.white' }}
        />
        <FormEvoCategorySelect
          onChange={setFormCategoryId}
          sx={{ width: '40%', flexShrink: 0, bgcolor: 'common.white' }}
        />
        {showWorktypeFilter && !hideLinkedToWorktypeCheckbox && (
          <MUIFormControlLabel
            control={
              <MUICheckbox checked={linkedToWorktype} onChange={(event) => setLinkedToWorktype(event.target.checked)} />
            }
            label="Linked to this worktype"
            sx={{ flexShrink: 0, m: 0 }}
          />
        )}
      </MUIStack>

      <MUIStack spacing={2} sx={cardsContainerSx}>
        {isLoading ? (
          <MUIStack alignItems="center" sx={{ py: 4 }}>
            <MUICircularProgress size={28} />
          </MUIStack>
        ) : hasError ? (
          <MUITypography variant="body2" sx={{ py: 2, textAlign: 'center', color: 'error.main' }}>
            Couldn&apos;t load forms. Please try again.
          </MUITypography>
        ) : forms.length === 0 ? (
          <MUITypography variant="body2" sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            No forms found.
          </MUITypography>
        ) : variant === 'radio' ? (
          <MUIRadioGroup value={selectedFormIds ?? ''} onChange={handleRadioChange}>
            <MUIStack spacing={1}>{renderCards()}</MUIStack>
          </MUIRadioGroup>
        ) : (
          <MUIStack spacing={1}>{renderCards()}</MUIStack>
        )}

        {isLoadingMore && (
          <MUIStack alignItems="center" sx={{ py: 1 }}>
            <MUICircularProgress size={24} />
          </MUIStack>
        )}
        <MUIBox ref={sentinelRef} sx={{ height: 1 }} />
      </MUIStack>
    </MUIStack>
  )
}

FormSearchAndSelect.propTypes = {
  variant: PropTypes.oneOf(VARIANTS).isRequired,
  selectedFormIds: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  workTypeId: PropTypes.string,
  pageSize: PropTypes.number,
  hideLinkedToWorktypeCheckbox: PropTypes.bool,
  sx: PropTypes.object,
  cardsContainerSx: PropTypes.object
}
